/**
 *   SessionExpirationWarning 
 *    - used throughout the app to warn when a user has < 30 minutes left on their session. 
 *    - Contains both the LoginModal and SessionExpirationWarningModal.
 *   SessionExpirationWarningModal
 *    - the modal that appears when a user has < 30 minutes left on their session.
 *   SessionExpirationCountdown 
 *    - used in distro since people often leave that tab open for a long period of time.
 *    - displays the amount of time as HHhMMm e.g "1h23m" left in the user session.
 *   LoginModal 
 *    - provides a modal wrapper around the Auth.tsx component so people don't have to login with a new tab.
 */

import AuthPage from '../Auth';
import { useCookies } from 'react-cookie';
import { useRef, useEffect, useState } from "react";
import { get_deployment, usePost } from "../API";
import { SessionExpirationWarningModal } from '@aidkitorg/component-library';
import { AuthPageModal } from './AuthPageModal';

function useDevTokenUpdater(): void {

  const [, setCookie] = useCookies(['auth_token', 'auth_token_expires']);

  const testExpiresIn = 20*60; // in seconds (20 minutes)
  if (process.env.NODE_ENV === 'development' || get_deployment() === 'idtest') {
    (window as any).updateCookieExpiration = (expiresIn: number = testExpiresIn) => {
      // update our auth_token_expires cookie. 
      // this won't affect the actual auth token, but it will trigger the modal.
      setCookie("auth_token_expires", Date.now() + expiresIn * 1000, {
        maxAge: expiresIn,
        path: "/",
        sameSite: "strict"
      })
    }
  }

}

export function SessionExpirationWarning() {

  const L = {
    'session_expiration_warning': {
    }
  }

  const [cookies, ] = useCookies(['auth_token', 'auth_token_expires']);

  // Provides a window function for dev testing
  useDevTokenUpdater();

  // State vars for managing the modal
  const [secondsRemaining, setSecondsRemaining] = useState<number>(86_400); // 24 hours in seconds
  const [open, setOpen] = useState<boolean>(false);
  // When the user closes the modal via 'Stay signed in' or 'Log out now', dont re-open it.
  const [userClosed, setUserClosed] = useState<boolean>(false);

  const [authPageModalOpen, setAuthPageModalOpen] = useState<boolean>(false);

  // Interval to check the auth token cookie
  const checkCookiesInterval = useRef<NodeJS.Timeout>();
  const checkCookiesIntervalMs = 10 * 1000; // every 10 seconds
  useEffect(() => {
    const checkCookie = () => {
      const cookie = cookies?.['auth_token_expires'];
      // This handles backwards compatability with before we had this cookie.
      // Or if the cookie doesnt exist at all, don't bother with the warning.
      if (!cookie) return undefined;

      // If we do have a cookie, it will actually expire and we won't get a response from the server
      // To get around this, we multiply our intervalMs by 2 to give some buffer.
      // In practice this means we will show the "session has expired" for 20s before it actually expires.
      const expiresAt = new Date(parseInt(cookie));
      const localSecondsRemaining = Math.max(Math.floor((expiresAt.getTime() - Date.now() - checkCookiesIntervalMs*2) / 1000), 0);
      setSecondsRemaining(localSecondsRemaining);
    }

    // On page load, and every 10 seconds after that, check the cookie.
    checkCookie();
    checkCookiesInterval.current = setInterval(() => {
      checkCookie();
    }, checkCookiesIntervalMs); 

    return () => clearInterval(checkCookiesInterval.current);
  }, [cookies]);

  useEffect(() => {
    if (userClosed) return;
    // If the user has never closed the modal, and the time remaining is less than 30 minutes, open the modal.
    const initialLoad = secondsRemaining <= 30 * 60;
    // If the amount of time remaining is less than 0, the user session has expired
    // Keep the modal open with a "Your session has expired" message
    const sessionExpired = secondsRemaining <= 0;
    // If the amount of time remaining is less than 60 seconds, open the modal
    const lastMinute = secondsRemaining > 0 && secondsRemaining < 60;

    if (initialLoad || sessionExpired || lastMinute) {
      // If the user is on the login page, or the /auth/google page, we don't need the modal
      setOpen(['/login','/auth/google'].every(p => !window.location.pathname.includes(p)))
    }
  }, [secondsRemaining, userClosed]);

  return <div className="hidden h-0">
    <SessionExpirationWarningModal 
      open={open} 
      secondsRemaining={secondsRemaining} 
      onExtendSession={() => {
        setUserClosed(true);
        setOpen(false);
        setAuthPageModalOpen(true);
      }}
      onLogout={() => {
        setUserClosed(true);
        window.location.href = '/logout';
      }}
    />
    <AuthPageModal open={authPageModalOpen} onClose={() => setAuthPageModalOpen(false)} 
      onLoginSuccess={() => setAuthPageModalOpen(false)} />
  </div>
}
